import React, { useState, useEffect, useMemo } from "react";
import T from '../../../components/common/T';
import { useNavigate, useLocation } from "react-router-dom"; 
import { usePurchaseOrder } from "../../../context/PurchaseOrderContext";
import DataTable from "../../../components/common/DataTables.jsx";
import TableActionButtons from "../../../components/common/TableActionButtons";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../../components/common/PageLayout";
import useModuleFilters from "../../../hooks/useModuleFilters";
import { 
  ClipboardDocumentListIcon, 
  ClockIcon, TruckIcon, CheckCircleIcon 
} from "@heroicons/react/24/outline";
import Price from "../../../components/common/Price";
import { formatDate } from "../../../utils/dateUtils";

export default function PurchaseOrderList() {
  const navigate = useNavigate();
  const location = useLocation(); 
  const { purchaseOrders = [], fetchPOs, deletePO } = usePurchaseOrder(); 
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const handleDelete = async (id, poNumber) => {
    if (window.confirm(`Are you sure you want to delete purchase order ${poNumber}?`)) {
      if (deletePO) await deletePO(id);
    }
  };

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
  } = useModuleFilters("purchase-orders", {
    statuses: [],
    projects: [],
  });

  useEffect(() => { 
    if(fetchPOs) fetchPOs(); 
  }, [fetchPOs, location.key]); 

  // --- STATS DASHBOARD CALCULATION ---
  const stats = useMemo(() => [
    {
      label: "Total Orders",
      value: purchaseOrders.length,
      icon: ClipboardDocumentListIcon,
      variant: "default",
      isActive: statusFilter === "All" || !statusFilter,
      onClick: () => { setStatusFilter("All"); setSearchTerm(""); },
    },
    {
      label: "Ordered / Pending",
      value: purchaseOrders.filter(p => p.status === "Ordered" || p.status === "Pending").length,
      icon: ClockIcon,
      variant: "blue",
      isActive: statusFilter === "Ordered" || statusFilter === "Pending",
      onClick: () => setStatusFilter(s => (s === "Ordered" || s === "Pending") ? "All" : "Ordered"),
    },
    {
      label: "Received",
      value: purchaseOrders.filter(p => p.status === "Received").length,
      icon: TruckIcon,
      variant: "orange",
      isActive: statusFilter === "Received",
      onClick: () => setStatusFilter(s => s === "Received" ? "All" : "Received"),
    },
    {
      label: "Billed / Completed",
      value: purchaseOrders.filter(p => p.status === "Billed" || p.status === "Completed").length,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: statusFilter === "Completed" || statusFilter === "Billed",
      onClick: () => setStatusFilter(s => (s === "Completed" || s === "Billed") ? "All" : "Completed"),
    },
  ], [purchaseOrders, statusFilter]);

  const filteredData = useMemo(() => {
    let result = [...purchaseOrders];
    if (statusFilter && statusFilter !== "All") {
      if (statusFilter === "Completed" || statusFilter === "Billed") {
        result = result.filter(po => {
          const s = (po.status || '').toLowerCase();
          return s === 'completed' || s === 'billed';
        });
      } else if (statusFilter === "Ordered" || statusFilter === "Pending") {
        result = result.filter(po => {
          const s = (po.status || '').toLowerCase();
          return s === 'ordered' || s === 'pending';
        });
      } else {
        result = result.filter(po => (po.status || '').toLowerCase() === statusFilter.toLowerCase());
      }
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(po => 
        (po.poNumber?.toString() || "").toLowerCase().includes(s) ||
        (po.vendorName?.toString() || "").toLowerCase().includes(s) ||
        (po.materialName?.toString() || "").toLowerCase().includes(s)
      );
    }
    return result;
  }, [purchaseOrders, statusFilter, searchTerm]);

  const getStatusColor = (status) => {
    switch (status) {
      case "Ordered": return "bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800/60";
      case "Dispatched": return "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/60"; 
      case "Received": return "bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800/60";
      case "Billed": return "bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-400 border-teal-200 dark:border-teal-800/60";
      case "Completed": return "bg-green-50 dark:bg-emerald-950/40 text-green-700 dark:text-emerald-400 border-green-200 dark:border-emerald-800/60";
      default: return "bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-700";
    }
  };

  const columns = [
    { header: "PO Number", accessor: "poNumber", sortable: true, render: (row) => <span className="font-semibold text-purple-700 dark:text-purple-400">{row.poNumber}</span> },
    { header: "Vendor", accessor: "vendorName", sortable: true, render: (row) => <span className="font-medium text-gray-900 dark:text-white">{row.vendorName}</span> },
    { header: "Material", accessor: "materialName", sortable: true, render: (row) => <span className="text-gray-700 dark:text-slate-300">{row.materialName || "Multiple Items"}</span> },
    { header: "Total", accessor: "totalPrice", sortable: true, align: "right", render: (row) => <span className="font-mono font-semibold text-gray-900 dark:text-white"><Price amount={Number(row.totalPrice || 0)} /></span> },
    { header: "Order Date", accessor: "createdAt", sortable: true, render: (row) => <span className="text-gray-600 dark:text-slate-400 text-xs">{formatDate(row.createdAt)}</span> },
    {
      header: "Status",
      accessor: "status",
      align: "center",
      sortable: true,
      render: (row) => (
        <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border ${getStatusColor(row.status)}`}>
          {row.status}
        </span>
      )
    },
    {
      header: "Actions",
      align: "right",
      render: (row) => (
        <TableActionButtons
          extraActions={
            row.status === 'Received' || row.status === 'Completed' ? (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/admin/material/purchase/bills/new', { state: { poData: row }, search: `?poId=${row.id}` });
                }}
                className="h-7 px-2.5 bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-400 rounded-lg border border-teal-200 dark:border-teal-800 text-[10px] font-bold hover:bg-teal-100 dark:hover:bg-teal-900/40 transition shadow-2xs cursor-pointer"
              >
                Create Bill
              </button>
            ) : row.status === 'Billed' ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-teal-700 dark:text-teal-400 font-bold bg-teal-50 dark:bg-teal-950/40 px-2 py-0.5 rounded border border-teal-200 dark:border-teal-800">
                BILLED
              </span>
            ) : null
          }
          onView={() => navigate(`/admin/material/purchase-orders/${row.id}`)}
          onDelete={() => handleDelete(row.id, row.poNumber)}
          viewTitle="View Order"
          deleteTitle="Delete Order"
        />
      )
    }
  ];

  return (
    <PageLayout>
      <PageHeader 
        icon={ClipboardDocumentListIcon} 
        iconColor="text-[#E8612C]" 
        title="Purchase Orders" 
        subtitle="Track and manage vendor purchase orders and billing" 
        onRefresh={() => fetchPOs && fetchPOs()}
      >
        <button
          type="button"
          onClick={() => navigate("/admin/material/purchase")}
          className="h-8 px-3 bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] rounded-lg text-[11px] font-bold uppercase hover:bg-gray-50 dark:hover:bg-slate-800 transition shadow-2xs flex items-center gap-1.5 text-gray-700 dark:text-slate-300 cursor-pointer"
        >
          <ClipboardDocumentListIcon className="w-3.5 h-3.5" /> <T>View Requests</T>
        </button>
      </PageHeader>

      {/* Stats */}
      <StatsGrid stats={stats} cols={4} />

      {/* Unified Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search order number or vendor..."
        tabs={['All', 'Ordered', 'Received', 'Billed', 'Completed']}
        activeTab={statusFilter}
        onTabChange={setStatusFilter}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      />

      {/* Collapsible Filter Panel */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        onClear={() => { setStatusFilter("All"); setSearchTerm(""); }}
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Order Status</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Statuses</T></option>
            {(filterOptions.statuses?.length > 0 ? filterOptions.statuses : ['Ordered', 'Received', 'Billed', 'Completed']).map(st => (
              <option key={st} value={st}>{st}</option>
            ))}
          </select>
        </div>
      </FilterPanel>

      {/* Table */}
      <TableCard>
        <DataTable data={filteredData} columns={columns} emptyMessage="No orders found." />
      </TableCard>
    </PageLayout>
  );
}